import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import L from "leaflet";
import * as types from "../../../constants/ComponentConstants";
import Row from '../Row';
import Col from '../Col';
import Button from '../Button';
import Fieldset from "../Fieldset";
import MapView from './MapView';
import FeatureRenderer from './FeatureRenderer';
import MapEditController, { MapEditModes } from './MapEditController';
import { RouteManualText, BevoorradingsManualText, LusReadOnlyManualText } from './manualTexts';
import { BOUNDS_PADDING } from './MapConstants';

// De kaart van een lus: RouteModel-weergave + de editmodes (route, bevoorradingszones).
// Praat met de pagina in RouteModel-termen; GPX-conversie blijft bij de pagehelper.
class TochtRouteMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            mode: MapEditModes.VIEW,
            zonesStatus: types.EditBevoorradingsZonesStatus.EDITING,
            routeDrawn: false
        };
        this.map = null;
        this.renderer = null;
        this.controller = null;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.routeModel !== this.props.routeModel && this.renderer)
            this.renderer.render(this.props.routeModel);
    }

    componentWillUnmount() {
        if (this.controller)
            this.controller.destroy();
        if (this.renderer)
            this.renderer.destroy();
    }

    handleMapReady = (map) => {
        this.map = map;
        this.renderer = new FeatureRenderer(map, { bevoorradingMarkerName: this.props.bevoorradingMarkerName });
        this.controller = new MapEditController({
            map,
            renderer: this.renderer,
            onRouteDrawnChanged: (routeDrawn) => this.setState({ routeDrawn }),
            onZonesStatusChanged: (zonesStatus) => this.setState({ zonesStatus })
        });
        this.renderer.render(this.props.routeModel);
    };

    fitToRoute() {
        const { bounds } = this.props;
        if (!this.map || !bounds || !bounds.length)
            return Promise.resolve();

        this.map.fitBounds(bounds, { padding: BOUNDS_PADDING, animate: false });

        const loadingTileLayers = [];
        this.map.eachLayer(layer => {
            if (layer instanceof L.GridLayer && layer.isLoading())
                loadingTileLayers.push(layer);
        });
        return Promise.all(loadingTileLayers.map(layer => new Promise(resolve => layer.once('load', resolve))));
    }

    handleStartDrawing = () => {
        this.props.onStartDrawing();
        // Eerst de kaart zichtbaar laten worden (unhide → invalidateSize),
        // dan pas tekenen starten — pannen/zoomen op een display:none-kaart gaat verloren.
        this.setState({ mode: MapEditModes.DRAW_ROUTE }, () =>
            this.controller.startDrawRoute(this.props.drawStartCenter));
    };

    handleConfirmDraw = () => {
        const feature = this.controller.confirmDrawRoute();
        this.renderer.setDirectionVisible(true);
        this.setState({ mode: MapEditModes.VIEW });
        this.props.onRouteDrawn(feature);
    };

    handleCancelDraw = () => {
        this.controller.cancelDrawRoute();
        this.renderer.setDirectionVisible(true);
        this.setState({ mode: MapEditModes.VIEW });
        this.props.onCancelEdit();
    };

    enterEditRoute = () => {
        this.setState({ mode: MapEditModes.EDIT_ROUTE });
        this.renderer.setDirectionVisible(false);
        this.controller.startEditRoute();
    };

    handleEditRoute = () => {
        this.props.onRequestEditRoute(this.enterEditRoute);
    };

    handleEditZones = () => {
        this.props.onMarkersEditStart();
        this.setState({ mode: MapEditModes.EDIT_ZONES });
        this.controller.startEditZones();
    };

    handleZonesAdd = () => this.controller.zonesAdd();
    handleZonesEdit = () => this.controller.zonesEdit();
    handleZonesRemove = () => this.controller.zonesRemove();

    handleConfirmEdit = () => {
        const model = this.controller.confirmEdit();
        this.renderer.setDirectionVisible(true);
        this.setState({ mode: MapEditModes.VIEW });
        this.props.onModelEdited(model);
    };

    handleCancelEdit = () => {
        this.controller.cancelEdit();
        this.renderer.setDirectionVisible(true);
        this.setState({ mode: MapEditModes.VIEW });
        this.props.onCancelEdit();
    };

    manualContent() {
        switch (this.state.mode) {
            case MapEditModes.DRAW_ROUTE:
            case MapEditModes.EDIT_ROUTE:
                return RouteManualText;
            case MapEditModes.EDIT_ZONES:
                return BevoorradingsManualText;
            default:
                return LusReadOnlyManualText;
        }
    }

    render() {
        const { mode, zonesStatus, routeDrawn } = this.state;
        const {
            routeModel, conflictLayers, bounds, baseLayerUrl, mapName, isLoading, legendeRows,
            routeEditable, editButtonDisabled, hasBevoorradingsZones,
            werkelijkeAfstand
        } = this.props;

        const isEditing = mode !== MapEditModes.VIEW;
        const hasGpx = !!routeModel;
        const hidden = !hasGpx && !isEditing;

        const werkelijkeAfstandTekst = 'Werkelijke afstand';
        const showWerkelijkeAfstand = werkelijkeAfstand !== undefined && !hidden && werkelijkeAfstand > 0 && !isEditing;

        const editingRouteButtons = (
            <Fieldset>
                <Row>
                    <Button
                        text={"Annuleer"}
                        onClick={mode === MapEditModes.DRAW_ROUTE ? this.handleCancelDraw : this.handleCancelEdit}>
                    </Button>
                    <Button
                        text={"Bevestig getekende route "}
                        onClick={mode === MapEditModes.DRAW_ROUTE ? this.handleConfirmDraw : this.handleConfirmEdit}
                        disabled={mode === MapEditModes.DRAW_ROUTE && !routeDrawn}>
                    </Button>
                </Row>
            </Fieldset>);

        const editingZonesButtons = (
            <Fieldset>
                <h4 className="edit-bevoorradings-zones__title">Bevoorradingszones</h4>
                <div className="edit-bevoorradings-zones__container">
                    <Button
                        text={"Annuleer"}
                        onClick={this.handleCancelEdit}>
                    </Button>
                    <Button
                        text={"Toevoegen"}
                        onClick={this.handleZonesAdd}
                        selected={zonesStatus === types.EditBevoorradingsZonesStatus.ADDING}>
                    </Button>
                    <Button
                        text={"Wijzigen"}
                        onClick={this.handleZonesEdit}
                        selected={zonesStatus === types.EditBevoorradingsZonesStatus.EDITING}>
                    </Button>
                    <Button
                        text={"Verwijderen"}
                        onClick={this.handleZonesRemove}
                        selected={zonesStatus === types.EditBevoorradingsZonesStatus.REMOVING}>
                    </Button>
                    <Button
                        text={"Bevestig"}
                        onClick={this.handleConfirmEdit}>
                    </Button>
                </div>
            </Fieldset>);

        const editButtons = (
            <Fieldset>
                <Row>
                    <Button
                        text={"Wijzig route op kaart"}
                        onClick={this.handleEditRoute}
                        disabled={editButtonDisabled}
                        style={{ float: 'right' }}>
                    </Button>
                    {routeModel && routeModel.routes && routeModel.routes.length ?
                        <Button
                            text={"Richting omkeren"}
                            onClick={this.props.onReverseRoute}
                            disabled={editButtonDisabled}
                            style={{ float: 'right' }}>
                        </Button> : null}
                    {hasBevoorradingsZones ?
                        <Button
                            text={"Bevoorradingszones toevoegen/wijzigen/verwijderen"}
                            onClick={this.handleEditZones}
                            disabled={editButtonDisabled}
                            style={{ float: 'right' }}>
                        </Button> : null}
                </Row>
            </Fieldset>);

        const startDrawingButtons = (
            <Fieldset>
                <Row>
                    <Col size={9}>
                        Start je tocht in België en heb je nog geen GPX bestand van je route dan kan je deze hier uittekenen.<br />
                        Start je tocht in het buitenland dan kan je enkel een GPX bestand opladen.
                    </Col>
                    <Button
                        text={"Teken route op kaart"}
                        onClick={this.handleStartDrawing}
                        disabled={editButtonDisabled}
                        style={{ float: 'right' }}>
                    </Button>
                </Row>
            </Fieldset>);

        return (
            <Fragment>
                <Row>
                    <MapView
                        mapName={mapName}
                        baseLayerUrl={baseLayerUrl}
                        bounds={bounds}
                        center={this.props.drawStartCenter}
                        zoom={12}
                        hidden={hidden}
                        isLoading={isLoading}
                        legendeRows={legendeRows}
                        manualContent={this.manualContent()}
                        geoJsonLayers={conflictLayers}
                        onMapReady={this.handleMapReady}
                    />
                </Row>
                {showWerkelijkeAfstand &&
                    <Row>
                        <Col size="12">
                            <p className="werkelijke-afstand"> {werkelijkeAfstandTekst}: {werkelijkeAfstand}km</p>
                        </Col>
                    </Row>
                }
                {routeEditable &&
                    (isEditing ?
                        (
                            ((mode === MapEditModes.DRAW_ROUTE || mode === MapEditModes.EDIT_ROUTE) && editingRouteButtons)
                            ||
                            (mode === MapEditModes.EDIT_ZONES && editingZonesButtons)
                        )
                        :
                        hasGpx ? editButtons : startDrawingButtons
                    )
                }
            </Fragment>
        );
    }
}

TochtRouteMap.propTypes = {
    routeModel: PropTypes.object,
    conflictLayers: PropTypes.array,
    bounds: PropTypes.array,
    baseLayerUrl: PropTypes.string.isRequired,
    mapName: PropTypes.string,
    isLoading: PropTypes.bool,
    legendeRows: PropTypes.array,
    routeEditable: PropTypes.bool,
    editButtonDisabled: PropTypes.bool,
    hasBevoorradingsZones: PropTypes.bool,
    bevoorradingMarkerName: PropTypes.string,
    werkelijkeAfstand: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    drawStartCenter: PropTypes.array,
    onReverseRoute: PropTypes.func,
    onStartDrawing: PropTypes.func.isRequired,
    onRouteDrawn: PropTypes.func.isRequired,
    onRequestEditRoute: PropTypes.func.isRequired,
    onMarkersEditStart: PropTypes.func.isRequired,
    onModelEdited: PropTypes.func.isRequired,
    onCancelEdit: PropTypes.func.isRequired
};

TochtRouteMap.defaultProps = {
    mapName: 'lus-route-map'
};

export default TochtRouteMap;
